import React, { createContext, useContext, useState } from 'react';
import useId from '@indeed/ihl-use-id';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import useResponsiveValue from '../../hooks/useResponsiveValue.js';
import '../../utils/warn.js';
import '../../hooks/useWindowSize.js';
import '../../hooks/useIsomorphicLayoutEffect.js';
import '@emotion/react';
import '../../hooks/useBreakpointIndex.js';

const DialogContext = /*#__PURE__*/createContext({});
const DialogProvider = DialogContext.Provider;
const useDialogContext = () => {
  return useContext(DialogContext);
};
/* eslint-disable @typescript-eslint/explicit-function-return-type */
const useDialog = _ref => {
  let {
    accessibleTrapProps,
    popupProps,
    defaultOpen,
    fullScreen = false,
    id,
    mobile = "auto",
    onOpenChange,
    open,
    portaled = false,
    size = "md",
    type = "dialog"
  } = _ref;
  const dialogId = useId(id);

  // Setup controllable state
  const [isOpen, setIsOpen] = useControllableState({
    state: open,
    defaultState: defaultOpen,
    initialState: false
  });
  const setOpen = React.useCallback(newIsOpen => {
    onOpenChange == null || onOpenChange(newIsOpen);
    setIsOpen(newIsOpen);
  }, [onOpenChange, setIsOpen]);

  // Set header description for context
  const [hasDescription, setHasDescription] = useState(false);

  // Update current dialog size with precedence of fullScreen > mobile > size
  const responsiveFullScreen = useResponsiveValue(fullScreen);
  const isMobile = useIsMobile(mobile);
  const responsiveSize = useResponsiveValue(size);

  // Setup render children function
  const toggle = React.useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen, setOpen]);

  // Setup context values
  return {
    accessibleTrapProps,
    popupProps,
    hasDescription,
    id: dialogId,
    isFullScreen: responsiveFullScreen,
    isMobile,
    isOpen,
    portaled,
    setHasDescription,
    setOpen,
    size: responsiveSize,
    toggle,
    type
  };
};

export { DialogProvider, useDialog, useDialogContext };
